// Teams SDK wrapper: detects Teams context once on first call, exposes a
// getAuthToken() for the Teams SSO path, and a getUserDisplayName() that
// reads from Teams' context instead of MSAL when embedded.

import { app as teamsApp, authentication } from '@microsoft/teams-js';

const TEAMS_INIT_TIMEOUT_MS = 1500;

let detected: 'in-teams' | 'browser' | null = null;
let detectPromise: Promise<'in-teams' | 'browser'> | null = null;

function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws; treat as iframe.
    return true;
  }
}

// Teams SDK v2 initialize() returns a promise that never resolves outside
// Teams (it's waiting for a handshake message from the host). Race it with a
// timeout so a plain browser doesn't hang the dashboard forever.
async function raceInit(): Promise<void> {
  return Promise.race<void>([
    teamsApp.initialize(),
    new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('teams init timeout')), TEAMS_INIT_TIMEOUT_MS),
    ),
  ]);
}

export async function detectTeamsContext(): Promise<'in-teams' | 'browser'> {
  if (detected) return detected;
  if (detectPromise) return detectPromise;

  detectPromise = (async () => {
    if (!isInIframe()) {
      detected = 'browser';
      return detected;
    }
    try {
      await raceInit();
      await teamsApp.getContext();
      detected = 'in-teams';
    } catch {
      detected = 'browser';
    }
    return detected;
  })();

  return detectPromise;
}

// Silent SSO: no popup, no user interaction. Returns an access token targeting
// the audience set in the Teams app manifest's webApplicationInfo.resource.
// Only works when:
//   - the Entra app's Application ID URI uses a tenant-verified hostname
//     (api://<subdomain>/<client-id>)
//   - the signed-in user has consented (admin consent counts)
//   - the Teams client IDs are pre-authorized on the scope
// Throws on failure; the caller decides whether to fall back to interactive.
export async function getTeamsAuthTokenSilent(): Promise<string> {
  return authentication.getAuthToken({ silent: true });
}

// Interactive path: Teams opens a dialog so the user can consent / re-auth.
// Use this as the fallback when silent fails (admin consent missing, stale
// cache, domain not verified, etc.).
export async function getTeamsAuthTokenInteractive(): Promise<string> {
  return authentication.getAuthToken({ silent: false });
}

// Preferred call site: tries silent first, falls back to interactive. Reports
// which path resolved so callers (and telemetry) can distinguish the happy
// path from the fallback.
export type TeamsAuthResult = {
  accessToken: string;
  path: 'silent' | 'interactive';
};

export async function getTeamsAuthToken(): Promise<TeamsAuthResult> {
  // Assume detectTeamsContext() already resolved 'in-teams' before we reach
  // this; initialize is a no-op on a second call.
  try {
    const accessToken = await getTeamsAuthTokenSilent();
    return { accessToken, path: 'silent' };
  } catch {
    const accessToken = await getTeamsAuthTokenInteractive();
    return { accessToken, path: 'interactive' };
  }
}

export async function getTeamsUserDisplayName(): Promise<string | null> {
  try {
    const ctx = await teamsApp.getContext();
    return ctx.user?.displayName ?? ctx.user?.userPrincipalName ?? null;
  } catch {
    return null;
  }
}

// Returns a synthetic-account shape derived from the Teams context, for cases
// where we want to surface "who is signed in" without an MSAL AccountInfo.
// Teams' user context contains oid + tenant.id + userPrincipalName + displayName.
export type TeamsUserContext = {
  oid: string;
  tenantId: string;
  userPrincipalName: string;
  displayName: string | null;
};

export async function getTeamsUserContext(): Promise<TeamsUserContext | null> {
  try {
    const ctx = await teamsApp.getContext();
    const user = ctx.user;
    if (!user?.id || !user?.tenant?.id || !user?.userPrincipalName) {
      return null;
    }
    return {
      oid: user.id,
      tenantId: user.tenant.id,
      userPrincipalName: user.userPrincipalName,
      // `||` not `??`: Teams returns an empty string (not null/undefined)
      // for displayName in external-unfamiliar contexts, and `??` treats ""
      // as defined and never falls through.
      displayName: user.displayName || user.userPrincipalName,
    };
  } catch {
    return null;
  }
}

// Teams sometimes strips `user.id`/`user.tenant.id` from the SDK context when
// it flags the app as "External unfamiliar" — but the SSO access token still
// carries those claims. Decode the JWT payload as a fallback identity source
// so the header (greeting, user menu) lights up even in the stripped-context
// case. Unverified parse only; the token is authoritative because the backend
// verifies its signature on every /api/* call.
export function unsafeDecodeTeamsUserFromToken(token: string): TeamsUserContext | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    const oid = typeof payload.oid === 'string' ? payload.oid : null;
    const tid = typeof payload.tid === 'string' ? payload.tid : null;
    const upn =
      (typeof payload.preferred_username === 'string' && payload.preferred_username) ||
      (typeof payload.upn === 'string' && payload.upn) ||
      null;
    if (!oid || !tid || !upn) return null;
    const name = typeof payload.name === 'string' ? payload.name : null;
    return {
      oid,
      tenantId: tid,
      userPrincipalName: upn,
      displayName: name || upn,
    };
  } catch {
    return null;
  }
}
